import {
  PopperProps,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
} from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';

import clsx from 'clsx';
import { DateTime } from 'luxon';

import CalendarIcon from '@watershed/icons/components/Calendar';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import ChevronLeftIcon from '@watershed/icons/components/ChevronLeft';
import ChevronRightIcon from '@watershed/icons/components/ChevronRight';
import useLocale from '@watershed/intl/frontend/useLocale';
import { DATE_FORMAT_MDY_TEXT } from '@watershed/shared-universal/constants';
import mixinSx from '@watershed/style/mixinSx';

import useToggle from '../../hooks/useToggle';

export type DatePickerProps = {
  date: DateTime | null;
  onChange: (dateTime: DateTime) => void;
  onNullValue?: () => void;
  disablePast?: boolean;
  /**
   * If false, we only disable past if the currently selected date is in the
   * future or there is no date selected. If true, always disable past.
   * Currently selected past date still renders fine, just not selectable.
   */
  disablePastForExistingDates?: boolean;
  prefix?: LocalizedString;
  id: string;
  disabled?: boolean;
  fullWidth?: boolean;
  minDate?: DateTime;
  maxDate?: DateTime;
  dateFormat?: string;
  fieldSx?: SxProps<Theme>;
  hideTopMargin?: boolean;
  fsUnmask?: boolean;
  readOnly?: boolean;
  defaultOpen?: boolean;
  'data-testid'?: string;
  // By default, uses day selector, but [year, month] allows selecting month
  views?: Array<'year' | 'month' | 'day'>;
  // Primarily for year/month views, which selector shows up first on click
  openTo?: 'year' | 'month' | 'day';
  // Props to pass to the Popper component for positioning customization
  PopperProps?: Partial<PopperProps>;
  // Callback when the picker is closed
  onClose?: () => void;
};

export default function DatePicker({
  date,
  onChange,
  onNullValue,
  disablePast = false,
  disablePastForExistingDates = false,
  prefix,
  id,
  disabled,
  fullWidth,
  minDate,
  maxDate,
  dateFormat = DATE_FORMAT_MDY_TEXT,
  fieldSx,
  hideTopMargin,
  fsUnmask,
  readOnly,
  defaultOpen = false,
  'data-testid': dataTestId,
  views,
  openTo = 'day',
  PopperProps: popperProps,
  onClose: onCloseProp,
}: DatePickerProps) {
  const locale = useLocale();
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleClose = () => {
    toggleOpen();
    onCloseProp?.();
  };

  // TODO (bryan): Show warning if the date is outside the bounds of the
  // min/max?

  return (
    // TODO: Luxon hardcodes first day of week as Monday
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
      <MuiDatePicker<DateTime>
        open={open}
        onOpen={toggleOpen}
        onClose={handleClose}
        minDate={minDate}
        maxDate={maxDate}
        views={views}
        openTo={openTo}
        renderInput={(params) => (
          <TextField
            id={id}
            {...params}
            onClick={!disabled ? toggleOpen : undefined}
            sx={mixinSx(fieldSx, {
              '& input': {
                padding: 0,
                ...(fullWidth ? {} : { maxWidth: '16ch' }),
              },
              '& .MuiInputBase-root': {
                cursor: 'pointer',
                maxHeight: 32,
                p: 1,
                marginTop: hideTopMargin ? 0 : undefined,
              },
              '& .MuiInputLabel-root': {
                color: 'text.primary',
              },
              '& .MuiInputBase-input': {
                maxHeight: '32px',
                height: '32px',
                pl: 0.5,
                ml: 0.5,
              },
              '& .MuiInputBase-input:focus, & .MuiInputBase-input:focus-visible, & .MuiInputBase-input:active':
                {
                  backgroundColor: 'transparent',
                },
            })}
            hiddenLabel={false}
            fullWidth={fullWidth}
            color="secondary"
            variant="outlined"
            slotProps={{
              input: {
                id,
                margin: 'none',
                startAdornment: (
                  <>
                    <CalendarIcon />
                    {prefix && (
                      <Typography
                        component="span"
                        children={prefix}
                        sx={{
                          color: (theme) => theme.palette.text.secondary,
                          lineHeight: '24px',
                          pl: '0.5em',
                          userSelect: 'none',
                        }}
                        id={id}
                      />
                    )}
                  </>
                ),
                endAdornment: <ChevronDownIcon />,
              },

              htmlInput: {
                ...params.inputProps,
                className: clsx(
                  params.inputProps?.className,
                  fsUnmask && 'fs-unmask'
                ),
                'data-testid': dataTestId,
              },
            }}
          />
        )}
        ToolbarComponent={() => null}
        components={{
          LeftArrowIcon: ChevronLeftIcon,
          RightArrowIcon: ChevronRightIcon,
          SwitchViewIcon: ChevronDownIcon,
        }}
        inputFormat={dateFormat}
        value={date}
        disabled={disabled}
        readOnly={readOnly}
        disableMaskedInput
        disablePast={
          disablePast &&
          (disablePastForExistingDates ||
            (date !== null && date >= DateTime.utc()))
        }
        onChange={(newDateInitial) => {
          // There's a bug in either MUI or Luxon where opening the date picker
          // with no value, then clicking the month/year setter, then clicking
          // any year (thus choosing a date) causes the value to be non-day-aligned.
          const newDate = newDateInitial?.startOf('day');
          if (!newDate || newDate === date) {
            onNullValue && !newDate && onNullValue();
            return;
          }
          onChange(newDate);
        }}
        PopperProps={popperProps}
      />
    </LocalizationProvider>
  );
}
